





















































































































import { computed, defineComponent, PropType, ref, toRef } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { Vue } from "vue/types/vue";
import { AiResponseMode } from "@scrile/api-provider/dist/api/AiSettingsProvider";
import { CallOptions } from "@scrile/api-provider/dist/api/CallOptionsProvider";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { UserPublicData } from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { Appointment } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentProvider";
import { AppointmentTypeKind } from "@scrile/api-provider/dist/projects/scrileConnect/AppointmentTypeProvider";
import { Message } from "@scrile/api-provider/dist/projects/scrileConnect/MessagesProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useKeepCustomScrollPosition from "@/hooks/useKeepCustomScrollPosition";
import useRole from "@/hooks/useRole";
import useVisualViewportFit from "@/hooks/useVisualViewportFit";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { getProfileUrl } from "@/lib/urlHelpers";
import { resolveUserName } from "@/lib/userHelpers";
import { isGetUserMediaSupported, isWebRTCSupported } from "@/lib/webrtcHelpers";
import config from "@/config";
import { BaseMenuItem, BlockingStatus, PredefinedMessage } from "@/types";
import { EVENTS } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIconButton from "@/components/BaseIconButton";
import BaseMenu from "@/components/BaseMenu";
import BasePage from "@/components/BasePage";
import BlockUserController from "@/components/BlockUserController";
import CustomScroll from "@/components/CustomScroll";
import MessageSendWithAttachment from "@/components/MessageSendWithAttachment";
import PageTitle from "@/components/PageTitle";
import UserBookAppointmentsModal from "@/components/UserBookAppointmentModal";
import useAIMessages from "@/views/PageMessages/hooks/useAIMessages";
import AiBanner from "./AiBanner";
import AiMessageGenerate from "./AiMessageGenerate";
import AiResponseConfirm from "./AiResponseConfirm/";
import AiResponsePlaceholder from "./AiResponsePlaceholder";
import GroupModal from "./GroupModal.vue";
import JoinAppointmentTooltip from "./JoinAppointmentTooltip";
import MessageDeleteConfirm from "./MessageDeleteConfirm.vue";
import MessageList from "./MessageList.vue";
import MessageSystemNote from "./MessageSystemNote.vue";
import SubscriptionOffer from "./SubscriptionOffer";

export default defineComponent({
  name: "MessagesView",
  components: {
    AiMessageGenerate,
    AiBanner,
    AiResponseConfirm,
    AiResponsePlaceholder,
    SubscriptionOffer,
    UserBookAppointmentsModal,
    JoinAppointmentTooltip,
    MessageDeleteConfirm,
    MessageList,
    BasePage,
    PageTitle,
    BaseAvatar,
    MessageSendWithAttachment,
    BlockUserController,
    BaseIconButton,
    BaseMenu,
    MessageSystemNote,
    GroupModal,
    CustomScroll,
  },
  props: {
    loading: { type: Boolean, required: true },
    allowResponse: { type: Boolean, required: true },
    messageThread: { type: Object as PropType<MessageThread | null> },
    messages: { type: Array as PropType<Message[]>, required: true },
    minUnreadMessageId: { type: String, required: true },
    opponentMinUnreadMessageId: { type: String, required: true },
    enableScroll: { type: Boolean, default: false },
    user: { type: Object as PropType<UserPublicData> },
    userCallOptions: { type: Object as PropType<CallOptions | null> },
    paymentPackages: { type: Array as PropType<PaymentPackage[] | null> },
    isBookingAvailable: { type: Boolean, required: true },
    isAppointmentStart: { type: Boolean, default: false },
    aiResponseEnabled: { type: Boolean, default: false },
    aiEnabled: { type: Boolean, default: false },
    showAiBanner: { type: Boolean, default: false },
    aiMode: { type: String as PropType<AiResponseMode>, default: AiResponseMode.SUGGESTION },
    forceFreeMessages: { type: Boolean, default: false },
    nearestAppointment: { type: Object as PropType<Appointment | null> },
  },
  setup(props, { emit }) {
    const list = ref<Vue | undefined>();
    const threadId = computed(() => props.messageThread?.id ?? undefined);
    const isAiCanResponse = computed(() => props.aiEnabled && props.aiResponseEnabled);
    const setTextInput = ref<((v: string) => void) | null>(null);

    const { authUser, authorities, isCreator, isMember } = useAuthUser();
    const { isAdmin: isUserAdmin } = useRole(toRef(props, "user"));
    const { scrollNode, scrollToBottom } = useKeepCustomScrollPosition(list, { keepScroll: true, behavior: "smooth" });
    const messagesRoot = ref<HTMLElement | undefined>();
    // iPhone keyboard: the chat fits the visible area above the keyboard and shows the last messages
    useVisualViewportFit(messagesRoot, opened => {
      if (opened) scrollToBottom("auto");
    });
    const {
      aiMessages,
      processing,
      showFillAIQuestionary,
      onGenerateAIClick,
      clearAIMessage,
      getPredefinedMessage,
    } = useAIMessages(threadId);

    const showAiConfirm = ref(false);
    const aiConfirmStatus = ref(false);
    const blockingStatus = ref<BlockingStatus>({ authUser: false, opponent: false });
    const blockingConfirm = ref(false);
    const showGroupModal = ref(false);
    const messageToDelete = ref<Message | null>(null);
    const showBookAppointmentsModal = ref(false);
    const predefinedMessage = ref<PredefinedMessage | null>(null);

    const userId = computed(() => props.user?.id ?? "");
    const isAutoAI = computed(() => props.aiMode === AiResponseMode.AUTO_RESPONSE);
    const screenName = computed(() => resolveUserName(props.user));
    const pinned = computed(() => !!props.messageThread?.pinTime);
    const hidden = computed(() => !!props.messageThread?.hidden);
    const titleComponent = computed(() => (props.user ? "router-link" : "div"));
    const profileTo = computed(() => (props.user ? getProfileUrl(props.user) : undefined));
    const isCallable = computed(
      () => config.featureCalling && props.user && userId.value !== authUser.value?.id && props.userCallOptions?.enabled
    );
    const isChatBlocked = computed(() => blockingStatus.value.opponent || blockingStatus.value.authUser);
    const isMessageSendShow = computed(
      () => props.user && !isChatBlocked.value && props.allowResponse && !(isAutoAI.value && isAiCanResponse.value)
    );
    const allowManageLists = computed(() => checkAuthorities(authorities.value, ["manageMyGroups"]));
    const noteText = computed(() =>
      blockingStatus.value.opponent
        ? $t("You blocked {0}.", [screenName.value])
        : $t("{0} blocked you. You can’t send messages to this creator anymore.", [screenName.value])
    );

    const menuItems = computed(() => {
      const menuItems: BaseMenuItem[] = [];
      if (props.messageThread && !isChatBlocked.value) {
        menuItems.push({
          name: "pin-user",
          text: pinned.value ? $t("Unmark as important") : $t("Mark as important"),
          prependIcon: "sci-star",
          ...(pinned.value
            ? {
                prependIconColor: "var(--palette-primary)",
                class: "__active",
              }
            : {}),
        });
      }
      if (!isMember.value) {
        if (allowManageLists.value) {
          menuItems.push({
            name: "add-to-list",
            class: "messages__list-icon __add-to-group",
            text: $t("Add to list"),
            prependIcon: "sci-round-plus",
          });
        }
        if (props.messageThread) {
          menuItems.push({
            name: "toggle-hide",
            class: "messages__list-icon" + (hidden.value ? " __restore-chat" : " __archive-chat"),
            text: hidden.value ? $t("Restore chat") : $t("Archive chat"),
            prependIcon: hidden.value ? "msr-unarchive" : "msr-archive",
          });
        }
        // blocking needs updateMyUserBlockingStatus, which only creators have in this fork
        if (isCreator.value && !isUserAdmin.value) {
          menuItems.push({
            name: "block-user",
            text: blockingStatus.value.opponent ? $t("Unblock user") : $t("Block user"),
            class: "negative",
            prependIcon: "sci-blocked",
            prependIconColor: "var(--palette-negative)",
          });
        }
      }
      return menuItems;
    });

    // leading edge: the call and its microphone request start inside the tap (iPhone Safari), repeated taps are ignored
    const handleCall = debounce(
      () => {
        const isWebRTCSupport = isWebRTCSupported() && isGetUserMediaSupported();
        if (isWebRTCSupport) {
          emitter.emit(EVENTS.CALL_OUTGOING, {
            subject: props.isAppointmentStart ? props.nearestAppointment : null,
            opponentUser: props.user,
          });
        } else {
          emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: $t("This browser does not support WebRTC") });
        }
      },
      1000,
      { leading: true, trailing: false }
    );

    const addMessage = (message: Message) => {
      emit("addMessage", message);
      clearAIMessage();
      scrollToBottom();
    };

    const receivedMessage = () => {
      if (!scrollNode.value) return;
      const bottomScrollTopPosition = scrollNode.value.scrollHeight - scrollNode.value.offsetHeight;
      if (
        bottomScrollTopPosition - scrollNode.value.scrollTop > 0 &&
        bottomScrollTopPosition - scrollNode.value.scrollTop < 100
      ) {
        scrollToBottom();
      }
    };

    const closeDeleteConfirm = () => {
      messageToDelete.value = null;
    };

    const handleDeleted = () => {
      if (messageToDelete.value) {
        emit("deleted", messageToDelete.value.id);
        closeDeleteConfirm();
      }
    };

    const toggleShowGroupModal = () => {
      showGroupModal.value = !showGroupModal.value;
    };

    const handleDisableAi = () => {
      showAiConfirm.value = true;
      aiConfirmStatus.value = false;
    };

    const handleEnableAi = () => {
      if (!props.aiResponseEnabled) {
        showAiConfirm.value = true;
        aiConfirmStatus.value = true;
      } else if (processing.value) {
        return;
      } else if (!isAutoAI.value) {
        onGenerateAIClick();
      }
    };

    const handleAiConfirmClick = () => {
      emit("update:aiResponse", { aiResponseEnabled: aiConfirmStatus.value }, () => {
        showAiConfirm.value = false;
      });
    };

    const setManualInput = (cb?: (v: string) => void) => {
      !!cb && (setTextInput.value = cb);
    };

    const pasteAiInput = (index: number = 0) => {
      predefinedMessage.value = getPredefinedMessage(index);
      setTextInput.value && setTextInput.value(predefinedMessage.value.text);
    };

    return {
      messagesRoot,
      showAiConfirm,
      aiConfirmStatus,
      isAiCanResponse,
      list,
      scrollNode,
      AppointmentTypeKind,
      isCreator,
      showBookAppointmentsModal,
      screenName,
      pinned,
      hidden,
      titleComponent,
      profileTo,
      blockingStatus,
      isChatBlocked,
      isCallable,
      showGroupModal,
      menuItems,
      blockingConfirm,
      userId,
      messageToDelete,
      noteText,
      isMessageSendShow,
      threadId,
      addMessage,
      handleCall,
      scrollToBottom,
      toggleShowGroupModal,
      receivedMessage,
      handleDeleted,
      handleDisableAi,
      handleEnableAi,
      handleAiConfirmClick,
      closeDeleteConfirm,
      aiMessages,
      isAutoAI,
      processing,
      showFillAIQuestionary,
      predefinedMessage,
      onGenerateAIClick,
      clearAIMessage,
      getPredefinedMessage,
      pasteAiInput,
      setManualInput,
    };
  },
  created() {
    this.$on("receivedMessage", this.receivedMessage);
  },
  beforeDestroy() {
    this.$off("receivedMessage", this.receivedMessage);
  },
});
